import { normalizeStrategyArtifactType, StrategyArtifactType, SwotStrategyLabel } from './growth-status.util';

export type RichStrategyArtifactKind = 'swot' | 'situationAssessment' | 'positioning' | 'marketingPlan';

export interface RichSwotFactor {
  type: string;
  title: string;
  description?: string;
  rank?: number;
  relativeWeight?: number;
  evidenceRefs?: unknown[];
}

export interface RichSwotPayload {
  strategyLabel?: SwotStrategyLabel | string;
  internalScore?: number;
  externalScore?: number;
  factors: RichSwotFactor[];
  situationSummary?: string;
  keyInsights: string[];
}

export interface RichSituationAssessmentPayload {
  marketContext?: string;
  competitiveLandscape?: string;
  customerSegmentsInsight?: string;
  channelReadiness?: string;
  riskFlags: string[];
  opportunityHorizon?: string;
  strategyLabel?: SwotStrategyLabel | string;
}

export interface RichPositioningPillar {
  title?: string;
  message?: string;
  supportingPoints?: string[];
}

export interface RichPositioningPayload {
  positioningStatement?: string;
  targetAudience?: string;
  frameOfReference?: string;
  pointOfDifference?: string;
  reasonToBelieve?: string;
  elevatorPitch?: string;
  valueProposition?: string;
  differentiators: string[];
  messagingPillars: RichPositioningPillar[];
  recommendedTonePreset?: string | number;
}

export interface RichMarketingPlanGoal {
  id?: string;
  title?: string;
  smart?: { measurable?: string; timeBound?: string };
}

export interface RichMarketingPlanKpi {
  id?: string;
  name?: string;
  metricKey?: string;
  targetValue?: unknown;
  unit?: string;
  periodDays?: unknown;
}

export interface RichMarketingPlanSegment {
  id?: string;
  name?: string;
  description?: string;
}

export interface RichMarketingPlanChannel {
  channelKey?: string;
  weight?: number;
  rationale?: string;
  organicFirst?: boolean;
}

export interface RichMarketingPlanPillar {
  title?: string;
  themes?: string[];
}

export interface RichMarketingPlanTimelineWeek {
  week?: number;
  milestones?: string[];
}

export interface RichMarketingPlanPayload {
  planningHorizonDays?: number;
  goals: RichMarketingPlanGoal[];
  kpis: RichMarketingPlanKpi[];
  segments: RichMarketingPlanSegment[];
  channelMix: RichMarketingPlanChannel[];
  contentPillars: RichMarketingPlanPillar[];
  timeline: RichMarketingPlanTimelineWeek[];
  risks: string[];
}

export function parseStrategyArtifactJson(artifactJson: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(artifactJson) as unknown;
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

export function detectRichStrategyArtifactKind(
  artifactJson: string,
  artifactType: unknown
): RichStrategyArtifactKind | null {
  const parsed = parseStrategyArtifactJson(artifactJson);
  if (!parsed) {
    return null;
  }

  if (Array.isArray(parsed['factors'])) {
    return 'swot';
  }

  const normalized = normalizeStrategyArtifactType(artifactType);

  if (
    normalized === 'SituationAssessment'
    || typeof parsed['marketContext'] === 'string'
    || (parsed['marketContext'] !== undefined && parsed['competitiveLandscape'] !== undefined)
  ) {
    return 'situationAssessment';
  }

  if (
    normalized === 'Positioning'
    || typeof parsed['positioningStatement'] === 'string'
    || parsed['positioningStatement'] !== undefined
  ) {
    return 'positioning';
  }

  if (
    normalized === 'MarketingPlan'
    || Array.isArray(parsed['channelMix'])
    || (Array.isArray(parsed['goals']) && parsed['planningHorizonDays'] !== undefined)
  ) {
    return 'marketingPlan';
  }

  return null;
}

export function isRichStrategyArtifact(artifactJson: string, artifactType: unknown): boolean {
  return detectRichStrategyArtifactKind(artifactJson, artifactType) !== null;
}

export function parseRichSwotPayload(artifactJson: string): RichSwotPayload | null {
  const parsed = parseStrategyArtifactJson(artifactJson);
  if (!parsed || !Array.isArray(parsed['factors'])) {
    return null;
  }

  return {
    strategyLabel: typeof parsed['strategyLabel'] === 'string' ? parsed['strategyLabel'] : undefined,
    internalScore: typeof parsed['internalScore'] === 'number' ? parsed['internalScore'] : undefined,
    externalScore: typeof parsed['externalScore'] === 'number' ? parsed['externalScore'] : undefined,
    factors: parsed['factors']
      .filter(item => item && typeof item === 'object')
      .map(item => {
        const factor = item as Record<string, unknown>;
        return {
          type: typeof factor['type'] === 'string' ? factor['type'] : '',
          title: typeof factor['title'] === 'string' ? factor['title'] : '',
          description: typeof factor['description'] === 'string' ? factor['description'] : undefined,
          rank: typeof factor['rank'] === 'number' ? factor['rank'] : undefined,
          relativeWeight: typeof factor['relativeWeight'] === 'number' ? factor['relativeWeight'] : undefined,
          evidenceRefs: Array.isArray(factor['evidenceRefs']) ? [...factor['evidenceRefs']] : undefined
        };
      }),
    situationSummary: typeof parsed['situationSummary'] === 'string' ? parsed['situationSummary'] : undefined,
    keyInsights: Array.isArray(parsed['keyInsights'])
      ? parsed['keyInsights'].filter(item => typeof item === 'string') as string[]
      : []
  };
}

export function swotFactorsByType(factors: RichSwotFactor[], type: string): RichSwotFactor[] {
  return factors.filter(f => f.type === type);
}

export function swotFactorTypeI18nKey(type: string): string {
  switch (type) {
    case 'InternalStrength':
      return 'growth.journey.swot.factorType.internalStrength';
    case 'InternalWeakness':
      return 'growth.journey.swot.factorType.internalWeakness';
    case 'ExternalOpportunity':
      return 'growth.journey.swot.factorType.externalOpportunity';
    case 'ExternalThreat':
      return 'growth.journey.swot.factorType.externalThreat';
    default:
      return 'growth.journey.swot.factorType.internalStrength';
  }
}

export const SWOT_FACTOR_TYPES = [
  'InternalStrength',
  'InternalWeakness',
  'ExternalOpportunity',
  'ExternalThreat'
] as const;

export function parseRichSituationAssessmentPayload(artifactJson: string): RichSituationAssessmentPayload | null {
  const parsed = parseStrategyArtifactJson(artifactJson);
  if (!parsed) {
    return null;
  }

  return {
    marketContext: stringField(parsed['marketContext']),
    competitiveLandscape: stringField(parsed['competitiveLandscape']),
    customerSegmentsInsight: stringField(parsed['customerSegmentsInsight']),
    channelReadiness: stringField(parsed['channelReadiness']),
    riskFlags: stringArray(parsed['riskFlags']),
    opportunityHorizon: stringField(parsed['opportunityHorizon']),
    strategyLabel: typeof parsed['strategyLabel'] === 'string' ? parsed['strategyLabel'] : undefined
  };
}

export function parseRichPositioningPayload(artifactJson: string): RichPositioningPayload | null {
  const parsed = parseStrategyArtifactJson(artifactJson);
  if (!parsed) {
    return null;
  }

  return {
    positioningStatement: stringField(parsed['positioningStatement']),
    targetAudience: stringField(parsed['targetAudience']),
    frameOfReference: stringField(parsed['frameOfReference']),
    pointOfDifference: stringField(parsed['pointOfDifference']),
    reasonToBelieve: stringField(parsed['reasonToBelieve']),
    elevatorPitch: stringField(parsed['elevatorPitch']),
    valueProposition: stringField(parsed['valueProposition']),
    differentiators: stringArray(parsed['differentiators']),
    messagingPillars: arrayOfObjects(parsed['messagingPillars']).map(item => ({
      title: stringField(item['title']),
      message: stringField(item['message']),
      supportingPoints: stringArray(item['supportingPoints'])
    })),
    recommendedTonePreset: parsed['recommendedTonePreset'] as string | number | undefined
  };
}

export function parseRichMarketingPlanPayload(artifactJson: string): RichMarketingPlanPayload | null {
  const parsed = parseStrategyArtifactJson(artifactJson);
  if (!parsed) {
    return null;
  }

  return {
    planningHorizonDays: typeof parsed['planningHorizonDays'] === 'number' ? parsed['planningHorizonDays'] : undefined,
    goals: arrayOfObjects(parsed['goals'] ?? parsed['objectives']).map(item => ({
      id: stringField(item['id']),
      title: stringField(item['title']),
      smart: item['smart'] && typeof item['smart'] === 'object'
        ? {
            measurable: stringField((item['smart'] as Record<string, unknown>)['measurable']),
            timeBound: stringField((item['smart'] as Record<string, unknown>)['timeBound'])
          }
        : undefined
    })),
    kpis: arrayOfObjects(parsed['kpis'] ?? parsed['kpiTargets']).map(item => ({
      id: stringField(item['id']),
      name: stringField(item['name']),
      metricKey: stringField(item['metricKey']),
      targetValue: item['targetValue'],
      unit: stringField(item['unit']),
      periodDays: item['periodDays']
    })),
    segments: arrayOfObjects(parsed['segments']).map(item => ({
      id: stringField(item['id']),
      name: stringField(item['name']),
      description: stringField(item['description'])
    })),
    channelMix: arrayOfObjects(parsed['channelMix']).map(item => ({
      channelKey: stringField(item['channelKey']),
      weight: typeof item['weight'] === 'number' ? item['weight'] : undefined,
      rationale: stringField(item['rationale']),
      organicFirst: item['organicFirst'] === true
    })),
    contentPillars: arrayOfObjects(parsed['contentPillars']).map(item => ({
      title: stringField(item['title']),
      themes: stringArray(item['themes'])
    })),
    timeline: arrayOfObjects(parsed['timeline']).map(item => ({
      week: typeof item['week'] === 'number' ? item['week'] : undefined,
      milestones: stringArray(item['milestones'])
    })),
    risks: stringArray(parsed['risks'])
  };
}

export function isSwotArtifactType(type: unknown): boolean {
  return normalizeStrategyArtifactType(type) === 'SWOT';
}

export function isSituationAssessmentArtifactType(type: unknown): boolean {
  return normalizeStrategyArtifactType(type) === 'SituationAssessment';
}

export function isPositioningArtifactType(type: unknown): boolean {
  return normalizeStrategyArtifactType(type) === 'Positioning';
}

export function isMarketingPlanArtifactType(type: unknown): boolean {
  return normalizeStrategyArtifactType(type) === 'MarketingPlan';
}

function stringField(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter(item => typeof item === 'string') as string[] : [];
}

function arrayOfObjects(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(item => item && typeof item === 'object') as Array<Record<string, unknown>>;
}

// --- Editable rich artifact models (user refinement before finalize) ---

export interface EditableSwotFactor {
  type: string;
  title: string;
  description: string;
  rank: number | null;
  relativeWeight: number | null;
  evidenceRefs?: unknown[];
}

export interface EditableSwotModel {
  strategyLabel: string;
  situationSummary: string;
  keyInsights: string[];
  factors: EditableSwotFactor[];
}

export interface EditableSituationModel {
  strategyLabel: string;
  marketContext: string;
  competitiveLandscape: string;
  customerSegmentsInsight: string;
  channelReadiness: string;
  opportunityHorizon: string;
  riskFlags: string[];
}

export interface EditablePositioningPillar {
  title: string;
  message: string;
  supportingPoints: string[];
}

export interface EditablePositioningModel {
  positioningStatement: string;
  targetAudience: string;
  frameOfReference: string;
  pointOfDifference: string;
  reasonToBelieve: string;
  elevatorPitch: string;
  valueProposition: string;
  differentiators: string[];
  messagingPillars: EditablePositioningPillar[];
  recommendedTonePreset: string | number | null;
}

export interface EditableMarketingPlanGoal {
  id?: string;
  title: string;
  measurable: string;
  timeBound: string;
}

export interface EditableMarketingPlanKpi {
  id?: string;
  name: string;
  metricKey: string;
  targetValue: string;
  unit: string;
  periodDays?: unknown;
  goalId?: string;
}

export interface EditableMarketingPlanSegment {
  id?: string;
  name: string;
  description: string;
  partySegmentRef?: string | null;
}

export interface EditableMarketingPlanChannel {
  channelKey: string;
  weight: number | null;
  rationale: string;
  organicFirst?: boolean;
}

export interface EditableMarketingPlanContentPillar {
  id?: string;
  title: string;
  themesJoined: string;
  targetSegments?: string[];
}

export interface EditableMarketingPlanTimelineWeek {
  week: number | null;
  milestonesJoined: string;
}

export interface EditableMarketingPlanModel {
  planningHorizonDays: number | null;
  goals: EditableMarketingPlanGoal[];
  kpis: EditableMarketingPlanKpi[];
  segments: EditableMarketingPlanSegment[];
  channelMix: EditableMarketingPlanChannel[];
  contentPillars: EditableMarketingPlanContentPillar[];
  timeline: EditableMarketingPlanTimelineWeek[];
  risks: string[];
}

export function deepCloneArtifactObject(original: Record<string, unknown> | null): Record<string, unknown> {
  if (!original) {
    return {};
  }

  return JSON.parse(JSON.stringify(original)) as Record<string, unknown>;
}

export function hydrateEditableSwotModel(parsed: RichSwotPayload | null): EditableSwotModel | null {
  if (!parsed) {
    return null;
  }

  return {
    strategyLabel: parsed.strategyLabel ?? '',
    situationSummary: parsed.situationSummary ?? '',
    keyInsights: [...parsed.keyInsights],
    factors: parsed.factors.map(factor => ({
      type: factor.type,
      title: factor.title,
      description: factor.description ?? '',
      rank: factor.rank ?? null,
      relativeWeight: factor.relativeWeight ?? null,
      evidenceRefs: factor.evidenceRefs ? [...factor.evidenceRefs] : undefined
    }))
  };
}

export function hydrateEditableSituationModel(parsed: RichSituationAssessmentPayload | null): EditableSituationModel | null {
  if (!parsed) {
    return null;
  }

  return {
    strategyLabel: parsed.strategyLabel ?? '',
    marketContext: parsed.marketContext ?? '',
    competitiveLandscape: parsed.competitiveLandscape ?? '',
    customerSegmentsInsight: parsed.customerSegmentsInsight ?? '',
    channelReadiness: parsed.channelReadiness ?? '',
    opportunityHorizon: parsed.opportunityHorizon ?? '',
    riskFlags: [...parsed.riskFlags]
  };
}

export function hydrateEditablePositioningModel(parsed: RichPositioningPayload | null): EditablePositioningModel | null {
  if (!parsed) {
    return null;
  }

  return {
    positioningStatement: parsed.positioningStatement ?? '',
    targetAudience: parsed.targetAudience ?? '',
    frameOfReference: parsed.frameOfReference ?? '',
    pointOfDifference: parsed.pointOfDifference ?? '',
    reasonToBelieve: parsed.reasonToBelieve ?? '',
    elevatorPitch: parsed.elevatorPitch ?? '',
    valueProposition: parsed.valueProposition ?? '',
    differentiators: [...parsed.differentiators],
    messagingPillars: parsed.messagingPillars.map(pillar => ({
      title: pillar.title ?? '',
      message: pillar.message ?? '',
      supportingPoints: [...(pillar.supportingPoints ?? [])]
    })),
    recommendedTonePreset: parsed.recommendedTonePreset ?? null
  };
}

export function hydrateEditableMarketingPlanModel(parsed: RichMarketingPlanPayload | null): EditableMarketingPlanModel | null {
  if (!parsed) {
    return null;
  }

  return {
    planningHorizonDays: parsed.planningHorizonDays ?? null,
    goals: parsed.goals.map(goal => ({
      id: goal.id,
      title: goal.title ?? '',
      measurable: goal.smart?.measurable ?? '',
      timeBound: goal.smart?.timeBound ?? ''
    })),
    kpis: parsed.kpis.map(kpi => ({
      id: kpi.id,
      name: kpi.name ?? '',
      metricKey: kpi.metricKey ?? '',
      targetValue: kpi.targetValue === null || kpi.targetValue === undefined ? '' : String(kpi.targetValue),
      unit: kpi.unit ?? '',
      periodDays: kpi.periodDays
    })),
    segments: parsed.segments.map(segment => ({
      id: segment.id,
      name: segment.name ?? '',
      description: segment.description ?? ''
    })),
    channelMix: parsed.channelMix.map(channel => ({
      channelKey: channel.channelKey ?? '',
      weight: channel.weight ?? null,
      rationale: channel.rationale ?? '',
      organicFirst: channel.organicFirst
    })),
    contentPillars: parsed.contentPillars.map(pillar => ({
      title: pillar.title ?? '',
      themesJoined: (pillar.themes ?? []).join(', ')
    })),
    timeline: parsed.timeline.map(week => ({
      week: week.week ?? null,
      milestonesJoined: (week.milestones ?? []).join('; ')
    })),
    risks: [...parsed.risks]
  };
}

function splitCommaList(value: string): string[] {
  return value.split(',').map(item => item.trim()).filter(Boolean);
}

function splitSemicolonList(value: string): string[] {
  return value.split(/[;,]/).map(item => item.trim()).filter(Boolean);
}

export function mergeRichSwotArtifact(original: Record<string, unknown> | null, model: EditableSwotModel): Record<string, unknown> {
  const merged = deepCloneArtifactObject(original);
  merged['strategyLabel'] = model.strategyLabel.trim() || merged['strategyLabel'];
  merged['situationSummary'] = model.situationSummary.trim() || undefined;
  merged['keyInsights'] = model.keyInsights.map(item => item.trim()).filter(Boolean);
  merged['factors'] = model.factors.map(factor => {
    const row: Record<string, unknown> = {
      type: factor.type,
      title: factor.title.trim()
    };
    const description = factor.description.trim();
    if (description) {
      row['description'] = description;
    }
    if (factor.rank !== null && factor.rank !== undefined) {
      row['rank'] = factor.rank;
    }
    if (factor.relativeWeight !== null && factor.relativeWeight !== undefined) {
      row['relativeWeight'] = factor.relativeWeight;
    }
    if (factor.evidenceRefs?.length) {
      row['evidenceRefs'] = factor.evidenceRefs;
    }
    return row;
  });
  return merged;
}

export function mergeRichSituationArtifact(original: Record<string, unknown> | null, model: EditableSituationModel): Record<string, unknown> {
  const merged = deepCloneArtifactObject(original);
  merged['strategyLabel'] = model.strategyLabel.trim() || merged['strategyLabel'];
  merged['marketContext'] = model.marketContext.trim() || undefined;
  merged['competitiveLandscape'] = model.competitiveLandscape.trim() || undefined;
  merged['customerSegmentsInsight'] = model.customerSegmentsInsight.trim() || undefined;
  merged['channelReadiness'] = model.channelReadiness.trim() || undefined;
  merged['opportunityHorizon'] = model.opportunityHorizon.trim() || undefined;
  merged['riskFlags'] = model.riskFlags.map(item => item.trim()).filter(Boolean);
  return merged;
}

export function mergeRichPositioningArtifact(original: Record<string, unknown> | null, model: EditablePositioningModel): Record<string, unknown> {
  const merged = deepCloneArtifactObject(original);
  merged['positioningStatement'] = model.positioningStatement.trim() || undefined;
  merged['targetAudience'] = model.targetAudience.trim() || undefined;
  merged['frameOfReference'] = model.frameOfReference.trim() || undefined;
  merged['pointOfDifference'] = model.pointOfDifference.trim() || undefined;
  merged['reasonToBelieve'] = model.reasonToBelieve.trim() || undefined;
  merged['elevatorPitch'] = model.elevatorPitch.trim() || undefined;
  merged['valueProposition'] = model.valueProposition.trim() || undefined;
  merged['differentiators'] = model.differentiators.map(item => item.trim()).filter(Boolean);
  merged['messagingPillars'] = model.messagingPillars.map(pillar => ({
    title: pillar.title.trim() || undefined,
    message: pillar.message.trim() || undefined,
    supportingPoints: pillar.supportingPoints.map(item => item.trim()).filter(Boolean)
  }));
  if (model.recommendedTonePreset !== null && model.recommendedTonePreset !== undefined && model.recommendedTonePreset !== '') {
    merged['recommendedTonePreset'] = model.recommendedTonePreset;
  }
  return merged;
}

export function mergeRichMarketingPlanArtifact(original: Record<string, unknown> | null, model: EditableMarketingPlanModel): Record<string, unknown> {
  const merged = deepCloneArtifactObject(original);
  if (model.planningHorizonDays !== null && model.planningHorizonDays !== undefined) {
    merged['planningHorizonDays'] = model.planningHorizonDays;
  }
  merged['goals'] = model.goals.map(goal => {
    const row: Record<string, unknown> = {};
    if (goal.id) {
      row['id'] = goal.id;
    }
    row['title'] = goal.title.trim() || undefined;
    row['smart'] = {
      measurable: goal.measurable.trim() || undefined,
      timeBound: goal.timeBound.trim() || undefined
    };
    return row;
  });
  merged['kpis'] = model.kpis.map(kpi => {
    const row: Record<string, unknown> = {};
    if (kpi.id) {
      row['id'] = kpi.id;
    }
    if (kpi.goalId) {
      row['goalId'] = kpi.goalId;
    }
    row['name'] = kpi.name.trim() || undefined;
    row['metricKey'] = kpi.metricKey.trim() || undefined;
    const target = kpi.targetValue.trim();
    if (target) {
      const numeric = Number(target);
      row['targetValue'] = Number.isFinite(numeric) && target === String(numeric) ? numeric : target;
    }
    row['unit'] = kpi.unit.trim() || undefined;
    if (kpi.periodDays !== undefined) {
      row['periodDays'] = kpi.periodDays;
    }
    return row;
  });
  merged['segments'] = model.segments.map(segment => {
    const row: Record<string, unknown> = {};
    if (segment.id) {
      row['id'] = segment.id;
    }
    row['name'] = segment.name.trim() || undefined;
    row['description'] = segment.description.trim() || undefined;
    if (segment.partySegmentRef) {
      row['partySegmentRef'] = segment.partySegmentRef;
    }
    return row;
  });
  merged['channelMix'] = model.channelMix.map(channel => {
    const row: Record<string, unknown> = {
      channelKey: channel.channelKey.trim() || undefined,
      rationale: channel.rationale.trim() || undefined
    };
    if (channel.weight !== null && channel.weight !== undefined) {
      row['weight'] = channel.weight;
    }
    if (channel.organicFirst) {
      row['organicFirst'] = true;
    }
    return row;
  });
  merged['contentPillars'] = model.contentPillars.map(pillar => {
    const row: Record<string, unknown> = {};
    if (pillar.id) {
      row['id'] = pillar.id;
    }
    row['title'] = pillar.title.trim() || undefined;
    row['themes'] = splitCommaList(pillar.themesJoined);
    if (pillar.targetSegments?.length) {
      row['targetSegments'] = pillar.targetSegments;
    }
    return row;
  });
  merged['timeline'] = model.timeline.map(week => ({
    week: week.week ?? undefined,
    milestones: splitSemicolonList(week.milestonesJoined)
  }));
  merged['risks'] = model.risks.map(item => item.trim()).filter(Boolean);
  return merged;
}

export function buildMergedRichArtifactJson(
  kind: RichStrategyArtifactKind,
  original: Record<string, unknown> | null,
  models: {
    swot?: EditableSwotModel | null;
    situation?: EditableSituationModel | null;
    positioning?: EditablePositioningModel | null;
    marketingPlan?: EditableMarketingPlanModel | null;
  }
): string {
  let merged: Record<string, unknown>;
  switch (kind) {
    case 'swot':
      merged = mergeRichSwotArtifact(original, models.swot!);
      break;
    case 'situationAssessment':
      merged = mergeRichSituationArtifact(original, models.situation!);
      break;
    case 'positioning':
      merged = mergeRichPositioningArtifact(original, models.positioning!);
      break;
    case 'marketingPlan':
      merged = mergeRichMarketingPlanArtifact(original, models.marketingPlan!);
      break;
  }
  return JSON.stringify(merged);
}
